/*
 * Password transport encryption shared with the server
 * (server/src/helpers/aesUtil.js): PBKDF2-HMAC-SHA256 over the session key
 * with a hex salt, then AES-CBC (PKCS#7) with a hex IV; ciphertext is base64.
 * Byte-compatible with the crypto-js 4.2 code it replaced.
 *
 * Uses the pure-JS @noble libraries rather than Web Crypto: browsers only
 * expose crypto.subtle on HTTPS or localhost, and this must also work when
 * development runs over plain http. (crypto.getRandomValues is available
 * everywhere.)
 */
import { cbc } from '@noble/ciphers/aes.js'
import { pbkdf2 } from '@noble/hashes/pbkdf2.js'
import { sha256 } from '@noble/hashes/sha2.js'

const hexToBytes = (hex) => new Uint8Array(hex.match(/../g).map(byte => parseInt(byte, 16)))

const bytesToHex = (bytes) => Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('')

const bytesToBase64 = (bytes) => {
  let binary = ''
  for (const byte of bytes) binary += String.fromCharCode(byte)
  return btoa(binary)
}

const base64ToBytes = (b64) => Uint8Array.from(atob(b64), char => char.charCodeAt(0))

// keySize is in bits (128 -> AES-128), as with the crypto-js version
const AesUtil = function (keySize, iterationCount) {
  this.keySizeBytes = keySize / 8
  this.iterationCount = iterationCount
}

// num bits / den -> that many random bytes, hex encoded
AesUtil.prototype.getRandomHex = (num, den) => bytesToHex(globalThis.crypto.getRandomValues(new Uint8Array(num / den)))

AesUtil.prototype.generateKey = function (salt, passPhrase) {
  return pbkdf2(sha256, new TextEncoder().encode(passPhrase), hexToBytes(salt), { c: this.iterationCount, dkLen: this.keySizeBytes })
}

AesUtil.prototype.encrypt = function (salt, iv, passPhrase, plainText) {
  const cipher = cbc(this.generateKey(salt, passPhrase), hexToBytes(iv))
  return bytesToBase64(cipher.encrypt(new TextEncoder().encode(plainText)))
}

AesUtil.prototype.decrypt = function (salt, iv, passPhrase, cipherText) {
  const cipher = cbc(this.generateKey(salt, passPhrase), hexToBytes(iv))
  return new TextDecoder().decode(cipher.decrypt(base64ToBytes(cipherText)))
}

export default AesUtil
